import { useModuleData } from "@/hooks/useModuleData";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { KpiCard } from "@/components/common/KpiCard";
import { Loader } from "@/components/common/Loader";
import { num, shortDate } from "@/lib/format";
import api from "@/lib/api";
import { FlaskConical, Check, X, ClipboardList } from "lucide-react";
import { toast } from "sonner";

export default function Quality() {
  const { data: lots, loading, reload } = useModuleData("/inspection-lots");
  const navigate = useNavigate();
  if (loading || !lots) return <Loader />;

  const decided = lots.filter((l) => l.result !== "OPEN");
  const passed = lots.filter((l) => l.result === "PASS").length;
  const failed = lots.filter((l) => l.result === "FAIL").length;
  const openLots = lots.filter((l) => l.result === "OPEN").length;
  const passRate = decided.length ? Math.round((passed / decided.length) * 100) : 100;

  const decide = async (lot, result) => {
    await api.patch(`/inspection-lots/${lot.id}/result?result=${result}`);
    toast.success(`Lot ${lot.lot_no} marked ${result}`);
    reload();
  };

  const columns = [
    { key: "lot_no", label: "Lot", render: (r) => <span className="font-mono text-xs text-[#EAB308]">{r.lot_no}</span> },
    { key: "material_name", label: "Product", render: (r) => (
      <div><div className="text-[#32363a]">{r.material_name}</div><div className="font-mono text-[10px] text-[#6a6d70]">{num(r.qty)} pcs</div></div>) },
    { key: "test_type", label: "Test" },
    { key: "value", label: "Measured", render: (r) => (
      <span className={`font-mono ${r.value < r.spec_min || r.value > r.spec_max ? "text-red-600" : "text-emerald-600"}`}>{r.value}</span>) },
    { key: "spec", label: "Spec", render: (r) => <span className="font-mono text-xs text-[#6a6d70]">{r.spec_min}–{r.spec_max}</span> },
    { key: "inspector", label: "Inspector" },
    { key: "date", label: "Date", render: (r) => shortDate(r.date) },
    { key: "recorded", label: "Recorded", render: (r) => <span className={`font-mono text-[10px] ${r.recorded ? "text-emerald-600" : "text-[#6a6d70]"}`}>{r.recorded ? "yes" : "—"}</span> },
    { key: "usage_decision", label: "UD", render: (r) => <span className="font-mono text-[10px] text-[#6a6d70]">{r.usage_decision || "—"}</span> },
    { key: "result", label: "Result", badge: true },
    { key: "actions", label: "", render: (r) => (
      <div className="flex gap-1.5" onClick={(e) => e.stopPropagation()}>
        <button data-testid={`qe01-open-${r.lot_no}`} onClick={() => navigate(`/quality/lot/${r.id}`)}
          className="inline-flex items-center gap-1 rounded-md border border-[#c8cfda] bg-white px-2 py-1 text-[11px] hover:border-[#EAB308]"><ClipboardList size={13} /> QE01</button>
        {r.result === "OPEN" && (<>
          <button data-testid={`qm-pass-${r.lot_no}`} onClick={() => decide(r, "PASS")}
            className="inline-flex items-center gap-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2 py-1 text-[11px] text-emerald-600 hover:border-emerald-500"><Check size={13} /> Pass</button>
          <button data-testid={`qm-fail-${r.lot_no}`} onClick={() => decide(r, "FAIL")}
            className="inline-flex items-center gap-1 rounded-md border border-red-500/40 bg-red-500/10 px-2 py-1 text-[11px] text-red-600 hover:border-red-500"><X size={13} /> Fail</button>
        </>)}
      </div>) },
  ];

  return (
    <div>
      <PageHeader title="Quality Management" subtitle="Inspection lots for tensile strength, bursting factor, print registration and seam integrity with pass/fail release." icon={FlaskConical} accent="#EAB308" />
      <div className="mb-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Pass Rate" value={`${passRate}%`} accent="#10B981" />
        <KpiCard label="Open Lots" value={openLots} accent="#F59E0B" />
        <KpiCard label="Passed" value={passed} accent="#10B981" />
        <KpiCard label="Failed" value={failed} accent="#EF4444" />
      </div>
      <DataTable testId="qm-lots-table" columns={columns} rows={lots} onRowClick={(r) => navigate(`/quality/lot/${r.id}`)} />
    </div>
  );
}
